import { BaseScreenFeature } from '../../../../features/base-screen/contract'
import { BaseImageOrder, BaseScreenSpecs, BaseScreenUploadOptions } from '../../../../types/base'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import type { HidChannel, HidReportListener } from '../../../../channels/hid/HidChannel'
import {
  sendBaseCommand,
  sendBaseRaw,
  readUtf8UntilFill,
  toBytes,
} from '../../protocols/base-command/command-io'
import type { BaseDockProfile } from '../../capabilities/base-command/profile'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { BaseDockEvents } from '../../../../events/BaseDockEvents'
import { BaseScreenPageService } from './BaseScreenPageService'

type GifuctFrame = {
  dims: { left?: number; top?: number; width: number; height: number }
  patch: Uint8ClampedArray | Uint8Array
  disposalType?: number
}

export type BaseScreenChannelResolver = (
  usagePage: number,
  usage: number,
) => Promise<HidChannel | null>

const SCREEN_WIDTH = 240
const SCREEN_HEIGHT = 135
const HEADER_DATA_LENGTH = 14
const SEND_DATA_LENGTH = 256
const MAX_GIF_SIZE = 24
// 屏幕素材传输使用 WebHID output report 0，不走 feature report。
const REPORT_ID_SCREEN = 0x00
// 1.0 发送器使用 10ms 轮询等待 receivFlag，3 秒未收到 inputreport 即超时。
const SCREEN_PACKET_ACK_TIMEOUT_MS = 3000
const SCREEN_PACKET_ACK_POLL_INTERVAL_MS = 10
const GIF_HEADER_DATA_DELAY_MS = 100

const ImageType = {
  STATIC: 1,
  ANIMATED: 2,
} as const

const ScreenCommand = {
  SEND_HEADER: 0x00,
  SEND_DATA: 0x01,
} as const

/**
 * 底座屏幕和素材传输实现。
 *
 * 这个类包含两类能力：
 * 1. 通过 feature report 读写屏幕配置，例如亮度、图片名、轮播顺序。
 * 2. 通过 output report 0 分包发送图片/GIF 素材。
 */
export class RapooBaseScreen extends BaseScreenFeature {
  /** ESPR8PRO/VPR8PLUS 图片数据通道，来自 WebHID usage=1 的独立 HID interface。 */
  private screenUploadChannel?: HidChannel
  private readonly maxScreenSlots: number
  private readonly systemPageSlots: readonly number[]
  private readonly systemSlots: ReadonlySet<number>
  pages: BaseScreenPageService

  constructor(
    device: BaseDevice,
    private readonly profile: BaseDockProfile,
    private readonly resolveAuxiliaryChannel?: BaseScreenChannelResolver,
  ) {
    super(device)
    const capability = device.manifest.capabilities.find((item) => item.type === 'baseScreen')
    this.maxScreenSlots = capability?.config.maxScreenSlots ?? 8
    const systemSlots = capability?.config.systemPages?.map((page) => page.slot) ?? []
    this.systemPageSlots = systemSlots
    this.systemSlots = new Set(systemSlots)
    this.pages = new BaseScreenPageService(
      {
        getImageName: (index) => this.getImageName(index),
        setImageName: (index, name) => this.setImageName(index, name),
        getImageOrder: () => this.getImageOrder(),
        setImageOrder: (showNum, order) => this.setImageOrder(showNum, order),
        getImageTimes: () => this.getImageTimes(),
        setImageTime: (index, seconds) => this.setImageTime(index, seconds),
      },
      {
        maxScreenSlots: this.maxScreenSlots,
        systemSlots,
      },
    )
  }

  /** 读取屏幕硬件规格位图。 */
  async getSpecs(): Promise<BaseScreenSpecs> {
    // 屏幕规格命令只在 VT_nrf54L_Base 的 P8/ESPR/VPR 系列存在。
    const command = this.requireCommand(this.commands.get_specs, 'getSpecs')
    const data = await sendBaseCommand(this.device, this.profile.config, command)
    const value = data.getUint8(1)
    return {
      value,
      lcd: Boolean(value & 0x01),
      radio433M: Boolean(value & 0x02),
      vbat: Boolean(value & 0x04),
    }
  }

  /** 读取指定图片槽位名称。 */
  async getImageName(index: number): Promise<string> {
    this.assertImageIndex(index)
    const data = await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x0c, index])
    return readUtf8UntilFill(data, 4, 32)
  }

  /** 写入指定图片槽位名称。 */
  async setImageName(index: number, name: string): Promise<void> {
    this.assertWritableImageIndex(index)
    if (typeof name !== 'string') {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Image name must be a string')
    }
    const bytes = Array.from(new TextEncoder().encode(name)).slice(0, 16)
    const nameField = bytes.slice(0, 16)
    while (nameField.length < 16) nameField.push(0)
    // 名称区固定 16 字节；空字符串写入 16 个 0，用于清空槽位名称。
    await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x12, 0x0b, index, ...nameField])
  }

  /** 删除指定图片槽位的素材。 */
  async deleteImage(index: number): Promise<void> {
    this.assertWritableImageIndex(index)
    await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x0e, index])
  }

  /** 读取屏幕亮度。 */
  async getScreenBrightness(): Promise<number> {
    const data = await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x01, 0x18])
    return data.getUint8(4)
  }

  /** 设置屏幕亮度。 */
  async setScreenBrightness(brightness: number): Promise<void> {
    this.assertRange(brightness, 0, 100, 'brightness')
    await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x06, brightness])
  }

  /** 读取轮播页面数量和顺序。 */
  async getImageOrder(): Promise<BaseImageOrder> {
    const data = await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x01, 0x0a])
    let imageNum = data.getUint8(4)
    // 固件恢复出厂后可能返回 0xff，1.0 兼容逻辑将它视为默认展示 2 个页面。
    if (imageNum === 0xff) imageNum = 2
    const order = Array.from(toBytes(data).slice(5, 13))
    return {
      imageNum,
      order,
      raw: toBytes(data),
    }
  }

  /** 写入轮播页面数量和顺序。 */
  async setImageOrder(showNum: number, order: number[] | Uint8Array): Promise<void> {
    this.assertRange(showNum, this.systemPageSlots.length, 8, 'showNum')
    const customSlots: number[] = []
    for (const slot of Array.from(order)) {
      if (
        Number.isInteger(slot) &&
        slot >= 0 &&
        slot < this.maxScreenSlots &&
        !this.systemSlots.has(slot) &&
        !customSlots.includes(slot)
      ) {
        customSlots.push(slot)
      }
    }
    for (let slot = 0; slot < this.maxScreenSlots; slot++) {
      if (!this.systemSlots.has(slot) && !customSlots.includes(slot)) {
        customSlots.push(slot)
      }
    }
    const normalized = [...this.systemPageSlots, ...customSlots].slice(0, 8)
    while (normalized.length < 8) normalized.push(0)
    await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x11, 0x09, showNum, ...normalized])
  }

  /** 读取各图片槽位轮播时间。 */
  async getImageTimes(): Promise<number[]> {
    const data = await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x1b])
    const times: number[] = []
    for (let index = 4; index < 19; index += 2) {
      times.push(data.getUint16(index, true))
    }
    return times
  }

  /** 设置指定图片槽位轮播时间。 */
  async setImageTime(index: number, seconds: number): Promise<void> {
    this.assertImageIndex(index)
    this.assertRange(seconds, 3, 65535, 'seconds')
    await sendBaseRaw(this.device, this.profile.config, [
      0xd0,
      0x05,
      0x1a,
      index,
      seconds & 0xff,
      (seconds >> 8) & 0xff,
    ])
  }

  /** 读取首页显示样式。 */
  async getIndexStyle(): Promise<number> {
    const data = await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x10])
    return data.getUint8(4)
  }

  /** 设置首页显示样式。 */
  async setIndexStyle(style: number): Promise<void> {
    this.assertRange(style, 0, 2, 'style')
    await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x03, 0x08, style])
  }

  /** 重置屏幕芯片。 */
  async resetScreenChip(): Promise<void> {
    await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x1c])
  }

  /** 上传单张静态图片，图片数据会转换为固件需要的 RGB565。 */
  async uploadStaticImage(
    index: number,
    imageData: ImageData,
    options: BaseScreenUploadOptions = {},
  ): Promise<void> {
    this.assertWritableImageIndex(index)
    // 上传图片会连续发送大量 report，必须作为一个原子任务，避免和普通读写命令交错。
    await this.device.runAtomic(async () => {
      const channel = await this.resolveScreenUploadChannel()
      const sender = this.createScreenSender(channel)
      try {
        const { rgb565, alphaData, hasAlpha } = this.convertImageData(imageData)
        await this.sendPictureHeader(
          sender,
          0,
          index,
          ImageType.STATIC,
          1,
          hasAlpha ? alphaData.length : 0,
        )
        await this.sendPictureTotalData(
          sender,
          this.concatenatePictureData(hasAlpha, rgb565, alphaData),
          {
            frameIndex: 0,
            frameCount: 1,
            onProgress: options.onProgress,
          },
        )
      } finally {
        sender.close()
      }
    })
  }

  /** 上传 GIF 帧序列，最多发送前 24 帧。 */
  async uploadGifFrames(
    index: number,
    frames: ImageData[],
    options: BaseScreenUploadOptions = {},
  ): Promise<void> {
    this.assertWritableImageIndex(index)
    if (frames.length === 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'GIF frames cannot be empty')
    }
    const limitedFrames = frames.slice(0, MAX_GIF_SIZE)
    await this.device.runAtomic(async () => {
      const channel = await this.resolveScreenUploadChannel()
      const sender = this.createScreenSender(channel)
      try {
        for (let frameIndex = 0; frameIndex < limitedFrames.length; frameIndex++) {
          const { rgb565, alphaData, hasAlpha } = this.convertImageData(limitedFrames[frameIndex])
          await this.sendPictureHeader(
            sender,
            frameIndex,
            index,
            ImageType.ANIMATED,
            limitedFrames.length,
            hasAlpha ? alphaData.length : 0,
          )
          // 1.0 实现里 GIF 帧头和帧数据之间必须停顿；屏幕芯片需要时间创建当前帧接收上下文。
          await delay(GIF_HEADER_DATA_DELAY_MS)
          await this.sendPictureTotalData(
            sender,
            this.concatenatePictureData(hasAlpha, rgb565, alphaData),
            {
              frameIndex,
              frameCount: limitedFrames.length,
              onProgress: options.onProgress,
            },
          )
        }
      } finally {
        sender.close()
      }
    })
  }

  /**
   * 解析屏幕素材上传使用的 HID 通道。
   *
   * ESPR8PRO/VPR8PLUS 的图片数据包必须发到 screenUpload 接口；
   * 具体 HID interface 查找交给对应底座 device 注入的辅助通道解析器。
   */
  private async resolveScreenUploadChannel(): Promise<HidChannel> {
    if (this.screenUploadChannel?.isConnected) {
      return this.screenUploadChannel
    }

    const config = this.getScreenUploadConfig()
    if (!config || !this.resolveAuxiliaryChannel) {
      return this.device.channel
    }

    const channel = await this.resolveAuxiliaryChannel(config.usagePage, config.usage)
    if (!channel) {
      throw new DeviceError(
        DeviceErrorCode.NOT_CONNECTED,
        `Screen upload interface not found: usagePage=${config.usagePage}, usage=${config.usage}. Please authorize the base again.`,
      )
    }

    this.screenUploadChannel = channel
    return channel
  }

  /** 从 manifest capability 读取屏幕上传接口配置。 */
  private getScreenUploadConfig(): { usagePage: number; usage: number } | null {
    const capability = this.device.manifest.capabilities.find((item) => item.type === 'baseScreen')
    const config = (capability?.config ?? {}) as Record<string, unknown>
    const usagePage = config.screenUsagePage
    const usage = config.screenUsage

    if (typeof usagePage !== 'number' || typeof usage !== 'number') {
      return null
    }
    return { usagePage, usage }
  }

  /** 从浏览器 File 自动识别静态图片或 GIF 并上传。 */
  async uploadFile(
    index: number,
    file: File,
    options: BaseScreenUploadOptions = {},
  ): Promise<void> {
    this.assertWritableImageIndex(index)
    // SDK 层只负责把 File 转成设备需要的 RGB565/GIF 帧数据；素材管理仍留给 Web 测试页或业务层。
    if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) {
      const frames = await this.parseAndScaleGif(file)
      await this.uploadGifFrames(index, frames, options)
      return
    }

    const imageData = await this.blobToScaledImageData(file)
    await this.uploadStaticImage(index, imageData, options)
  }

  /** 将 Canvas ImageData 转为固件屏幕使用的 RGB565 小端字节流。 */
  private convertImageData(imageData: ImageData): {
    rgb565: Uint8Array
    alphaData: Uint8Array
    hasAlpha: boolean
  } {
    // 固件当前只消费 RGB565 小端数据；alpha 通道保留在返回结构里，但 hasAlpha 固定为 false。
    const pixelCount = SCREEN_WIDTH * SCREEN_HEIGHT
    const rgb565 = new Uint8Array(pixelCount * 2)
    const alphaData = new Uint8Array(pixelCount)
    let rgbIndex = 0
    let alphaIndex = 0

    for (let index = 0; index < imageData.data.length; index += 4) {
      const r = imageData.data[index]
      const g = imageData.data[index + 1]
      const b = imageData.data[index + 2]
      const a = imageData.data[index + 3]
      const pixel = ((r & 0xf8) << 8) | ((g & 0xfc) << 3) | ((b & 0xf8) >> 3)

      rgb565[rgbIndex++] = pixel & 0xff
      rgb565[rgbIndex++] = (pixel >> 8) & 0xff
      alphaData[alphaIndex++] = a
    }

    return { rgb565, alphaData, hasAlpha: false }
  }

  /** 拼接图片描述头和像素数据，得到屏幕协议内部的完整图片 payload。 */
  private concatenatePictureData(
    haveAlpha: boolean,
    rgb565: Uint8Array,
    alphaData?: Uint8Array,
  ): Uint8Array {
    // 图片总数据格式：14 字节图片描述头 + RGB565 数据 + 可选 Alpha 数据。
    const rgbDataLength = SCREEN_WIDTH * SCREEN_HEIGHT * 2
    const alphaDataLength = haveAlpha && alphaData ? SCREEN_WIDTH * SCREEN_HEIGHT : 0
    const totalImageData = new Uint8Array(HEADER_DATA_LENGTH + rgbDataLength + alphaDataLength)
    let offset = 0

    offset = writeUInt16LE(totalImageData, offset, SCREEN_WIDTH)
    offset = writeUInt16LE(totalImageData, offset, SCREEN_HEIGHT)
    offset = writeUInt16LE(totalImageData, offset, 0)
    offset = writeUInt32LE(totalImageData, offset, rgbDataLength)
    offset = writeUInt32LE(totalImageData, offset, alphaDataLength)

    totalImageData.set(rgb565, offset)
    offset += rgb565.length
    if (alphaDataLength > 0 && alphaData) {
      totalImageData.set(alphaData, offset)
    }
    return totalImageData
  }

  /** 发送一帧图片的元信息包，例如槽位、图片类型、帧序号和总帧数。 */
  private async sendPictureHeader(
    sender: ScreenPacketSender,
    frameIndex: number,
    imageIndex: number,
    imageType: number,
    imageCount: number,
    imageAlphaDataLength: number,
  ): Promise<void> {
    // 屏幕协议包固定以 55 10 3A 开头，0D 0A 结尾，中间带累加和。
    const sendBuffer = new Uint8Array(16)
    sendBuffer[0] = 0x55
    sendBuffer[1] = 0x10
    sendBuffer[2] = 0x3a
    sendBuffer[3] = 0x08
    sendBuffer[4] = 0x00
    sendBuffer[5] = ScreenCommand.SEND_HEADER
    sendBuffer[6] = imageIndex
    sendBuffer[7] = imageType
    sendBuffer[8] = frameIndex
    sendBuffer[9] = imageCount
    sendBuffer[10] = 0x00

    const dataLength =
      imageAlphaDataLength > 0
        ? SCREEN_WIDTH * SCREEN_HEIGHT * 3 + HEADER_DATA_LENGTH
        : SCREEN_WIDTH * SCREEN_HEIGHT * 2 + HEADER_DATA_LENGTH
    sendBuffer[11] = Math.ceil(dataLength / 256)
    sendBuffer[12] = calcChecksum(sendBuffer, 3, 12)
    sendBuffer[13] = 0x0d
    sendBuffer[14] = 0x0a
    await sender.send(sendBuffer, {
      command: 'header',
      frameIndex,
      chunkIndex: null,
    })
  }

  /** 按 256 字节分块发送图片像素数据，并上报总进度。 */
  private async sendPictureTotalData(
    sender: ScreenPacketSender,
    totalImageData: Uint8Array,
    options: { frameIndex: number; frameCount: number; onProgress?: (progress: number) => void },
  ): Promise<void> {
    const totalLength = totalImageData.length
    let sentLength = 0
    let chunkIndex = 0

    while (sentLength < totalLength) {
      // 固件每包最多接收 256 字节有效图片数据，外层再包一层屏幕传输协议头。
      const currentChunkLength = Math.min(totalLength - sentLength, SEND_DATA_LENGTH)
      const sendBuffer = new Uint8Array(currentChunkLength + 13)
      sendBuffer[0] = 0x55
      sendBuffer[1] = 0x10
      sendBuffer[2] = 0x3a
      const dataLengthWithHeader = currentChunkLength + 3
      sendBuffer[3] = dataLengthWithHeader & 0xff
      sendBuffer[4] = (dataLengthWithHeader >> 8) & 0xff
      sendBuffer[5] = ScreenCommand.SEND_DATA
      sendBuffer[6] = chunkIndex

      const chunkData = totalImageData.slice(sentLength, sentLength + currentChunkLength)
      sendBuffer.set(chunkData, 7)
      sendBuffer[7 + currentChunkLength] = calcChecksum(sendBuffer, 3, 7 + currentChunkLength)
      sendBuffer[8 + currentChunkLength] = 0x0d
      sendBuffer[9 + currentChunkLength] = 0x0a

      await sender.send(sendBuffer, {
        command: 'data',
        frameIndex: options.frameIndex,
        chunkIndex,
      })
      sentLength += currentChunkLength
      chunkIndex++

      const progress = Math.round(
        ((options.frameIndex * totalLength + sentLength) / (options.frameCount * totalLength)) *
          100,
      )
      options.onProgress?.(progress)
      this.device.emit(BaseDockEvents.ScreenUploadProgress, {
        progress,
        frameIndex: options.frameIndex,
      })
    }
  }

  /** 创建屏幕分包发送器，封装“等上一包 ACK 再发下一包”的节流逻辑。 */
  private createScreenSender(channel: HidChannel): ScreenPacketSender {
    let receiveReady = true

    const handler: HidReportListener = () => {
      receiveReady = true
    }

    channel.onInputReport(handler)

    return {
      send: async (packet: Uint8Array, context: ScreenPacketContext) => {
        // 这里刻意贴近 1.0 ScreenDataToDevice.ts：inputreport 只把 receivFlag 置 true，
        // 下一包发送前按 10ms 轮询等待，避免 ACK 语义和老固件预期出现偏差。
        if (!receiveReady) {
          await waitForScreenReceiveReady(() => receiveReady, context)
        }
        receiveReady = false
        await channel.sendOutputReport(REPORT_ID_SCREEN, packet)
      },
      close: () => {
        channel.offInputReport(handler)
      },
    }
  }

  /** 将普通图片 Blob 缩放到 240x135，并读取成 ImageData。 */
  private async blobToScaledImageData(
    blob: Blob,
    targetWidth = SCREEN_WIDTH,
    targetHeight = SCREEN_HEIGHT,
  ): Promise<ImageData> {
    const imageBitmap = await createImageBitmap(blob, {
      resizeWidth: targetWidth,
      resizeHeight: targetHeight,
      resizeQuality: 'high',
    })
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const context = canvas.getContext('2d')
    if (!context) {
      imageBitmap.close()
      throw new DeviceError(DeviceErrorCode.READ_FAILED, 'Cannot create 2D canvas context')
    }

    try {
      context.drawImage(imageBitmap, 0, 0, targetWidth, targetHeight)
      return context.getImageData(0, 0, targetWidth, targetHeight)
    } finally {
      imageBitmap.close()
    }
  }

  /** 解析 GIF 文件，取前 24 帧并统一缩放到 240x135。 */
  private async parseAndScaleGif(file: File): Promise<ImageData[]> {
    // GIF 最多取前 24 帧，与 1.0 的 MAX_GIF_SIZE 保持一致，避免超过固件存储能力。
    const { parseGIF, decompressFrames } = await import('gifuct-js')
    const arrayBuffer = await file.arrayBuffer()
    const gif = parseGIF(arrayBuffer)
    const frames = decompressFrames(gif, true) as GifuctFrame[]
    const sourceSize = readGifLogicalScreenSize(arrayBuffer, frames)
    const sourceCanvas = document.createElement('canvas')
    const sourceContext = sourceCanvas.getContext('2d')
    const patchCanvas = document.createElement('canvas')
    const patchContext = patchCanvas.getContext('2d')
    const targetCanvas = document.createElement('canvas')
    const targetContext = targetCanvas.getContext('2d')

    if (!sourceContext || !patchContext || !targetContext) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, 'Cannot create canvas context')
    }

    sourceCanvas.width = sourceSize.width
    sourceCanvas.height = sourceSize.height
    targetCanvas.width = SCREEN_WIDTH
    targetCanvas.height = SCREEN_HEIGHT
    // 设备端当前不消费 alpha；这里先铺底，避免透明区域被 RGB565 转换成黑色条纹。
    sourceContext.fillStyle = '#000000'
    sourceContext.fillRect(0, 0, sourceCanvas.width, sourceCanvas.height)

    return frames.slice(0, MAX_GIF_SIZE).map((frame) => {
      const left = frame.dims.left ?? 0
      const top = frame.dims.top ?? 0
      const restoreImageData =
        frame.disposalType === 3
          ? sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height)
          : null
      const frameData = sourceContext.createImageData(frame.dims.width, frame.dims.height)
      frameData.data.set(frame.patch)
      patchCanvas.width = frame.dims.width
      patchCanvas.height = frame.dims.height
      patchContext.putImageData(frameData, 0, 0)
      // gifuct-js 返回的 patch 经常只是局部更新区域；用 drawImage 合成才能保留透明像素下的上一帧内容。
      sourceContext.drawImage(patchCanvas, left, top)
      targetContext.clearRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT)
      targetContext.drawImage(
        sourceCanvas,
        0,
        0,
        sourceCanvas.width,
        sourceCanvas.height,
        0,
        0,
        SCREEN_WIDTH,
        SCREEN_HEIGHT,
      )
      const scaledFrame = targetContext.getImageData(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT)

      if (frame.disposalType === 2) {
        sourceContext.clearRect(left, top, frame.dims.width, frame.dims.height)
        sourceContext.fillStyle = '#000000'
        sourceContext.fillRect(left, top, frame.dims.width, frame.dims.height)
      } else if (restoreImageData) {
        sourceContext.putImageData(restoreImageData, 0, 0)
      }

      return scaledFrame
    })
  }

  /** 当前芯片底座命令表。 */
  private get commands() {
    return this.profile.commands
  }

  /** 读取芯片可选命令，缺失时给出明确的不支持错误。 */
  private requireCommand(
    command: readonly number[] | undefined,
    feature: string,
  ): readonly number[] {
    if (!command) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        `${feature} is not supported by this base`,
      )
    }
    return command
  }

  /** 校验图片槽位，范围由 manifest 的 maxScreenSlots 声明。 */
  private assertImageIndex(index: number): void {
    this.assertRange(index, 0, this.maxScreenSlots - 1, 'image index')
  }

  /** 系统页面内容由固件维护，不允许素材写入、名称覆盖或删除。 */
  private assertWritableImageIndex(index: number): void {
    this.assertImageIndex(index)
    if (this.systemSlots.has(index)) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `Screen slot ${index} is a read-only system page`,
      )
    }
  }

  /** 通用数值范围校验，用于提前给出可读错误。 */
  private assertRange(value: number, min: number, max: number, label: string): void {
    if (!Number.isFinite(value) || value < min || value > max) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `${label} must be between ${min} and ${max}`,
      )
    }
  }
}

type ScreenPacketSender = {
  /** 发送一个已经组好的屏幕协议包。 */
  send(packet: Uint8Array, context: ScreenPacketContext): Promise<void>
  /** 上传结束后释放 input report 监听，避免残留监听影响后续流程。 */
  close(): void
}

type ScreenPacketContext = {
  command: 'header' | 'data'
  frameIndex: number
  chunkIndex: number | null
}

/** 写入 16 位小端整数，返回下一个写入偏移。 */
function writeUInt16LE(target: Uint8Array, offset: number, value: number): number {
  target[offset] = value & 0xff
  target[offset + 1] = (value >> 8) & 0xff
  return offset + 2
}

/** 写入 32 位小端整数，返回下一个写入偏移。 */
function writeUInt32LE(target: Uint8Array, offset: number, value: number): number {
  target[offset] = value & 0xff
  target[offset + 1] = (value >> 8) & 0xff
  target[offset + 2] = (value >> 16) & 0xff
  target[offset + 3] = (value >> 24) & 0xff
  return offset + 4
}

/** 计算屏幕协议使用的低 8 位累加和。 */
function calcChecksum(buffer: Uint8Array, start: number, endExclusive: number): number {
  let total = 0
  for (let index = start; index < endExclusive; index++) {
    total += buffer[index]
  }
  return total & 0xff
}

function readGifLogicalScreenSize(
  arrayBuffer: ArrayBuffer,
  frames: GifuctFrame[],
): { width: number; height: number } {
  const data = new DataView(arrayBuffer)
  if (data.byteLength >= 10) {
    const width = data.getUint16(6, true)
    const height = data.getUint16(8, true)
    if (width > 0 && height > 0) return { width, height }
  }

  return frames.reduce(
    (size, frame) => ({
      width: Math.max(size.width, (frame.dims.left ?? 0) + frame.dims.width),
      height: Math.max(size.height, (frame.dims.top ?? 0) + frame.dims.height),
    }),
    { width: SCREEN_WIDTH, height: SCREEN_HEIGHT },
  )
}

/** 按 1.0 receivFlag 语义轮询等待设备 ACK；报错带上帧/包信息方便定位固件停在哪一步。 */
async function waitForScreenReceiveReady(
  isReady: () => boolean,
  context: ScreenPacketContext,
): Promise<void> {
  let elapsed = 0
  while (!isReady() && elapsed < SCREEN_PACKET_ACK_TIMEOUT_MS) {
    await delay(SCREEN_PACKET_ACK_POLL_INTERVAL_MS)
    elapsed += SCREEN_PACKET_ACK_POLL_INTERVAL_MS
  }

  if (!isReady()) {
    throw new DeviceError(
      DeviceErrorCode.TIMEOUT,
      `Screen packet acknowledgement timeout (${formatScreenPacketContext(context)})`,
    )
  }
}

function formatScreenPacketContext(context: ScreenPacketContext): string {
  if (context.command === 'header') {
    return `frame=${context.frameIndex}, command=header`
  }
  return `frame=${context.frameIndex}, command=data, chunk=${context.chunkIndex}`
}

/** 简单延迟工具，用于 GIF 帧之间给固件留处理间隔。 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
